import React, { useEffect, useState } from 'react';
import SchoolLayout from '../../components/SchoolLayout';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Building2, Save } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Profile = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [school, setSchool] = useState(null);
  const [formData, setFormData] = useState({
    name: '', head_coach_name: '', phone: '', email: '', division: '', district: '', training_address: '', gelanggang: ''
  });

  useEffect(() => { fetchSchool(); }, []);

  const fetchSchool = async () => {
    try {
      const response = await axios.get(`${API_URL}/schools/${user.school_id}`);
      setSchool(response.data);
      setFormData({
        name: response.data.name, head_coach_name: response.data.head_coach_name,
        phone: response.data.phone, email: response.data.email,
        division: response.data.division, district: response.data.district,
        training_address: response.data.training_address,
        gelanggang: response.data.gelanggang || ''
      });
    } catch (error) {
      toast.error('Gagal memuatkan maklumat perguruan');
    } finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API_URL}/schools/${user.school_id}`, formData);
      toast.success('Maklumat perguruan berjaya dikemaskini');
      fetchSchool();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal mengemaskini maklumat');
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <SchoolLayout>
        <div className="flex items-center justify-center py-20">
          <div className="text-sm text-[#999] animate-pulse">Memuatkan...</div>
        </div>
      </SchoolLayout>
    );
  }

  const field = (id, label, type = 'text', colSpan = '') => (
    <div className={colSpan}>
      <Label htmlFor={id} className="text-xs font-bold text-[#555] uppercase tracking-wider">{label}</Label>
      <Input
        id={id}
        type={type}
        value={formData[id]}
        onChange={e => setFormData({ ...formData, [id]: e.target.value })}
        required
        className="mt-1.5 border-[#E5E5E5] text-sm"
        data-testid={`input-${id}`}
      />
    </div>
  );

  return (
    <SchoolLayout>
      <div className="space-y-5 max-w-3xl">

        {/* Header */}
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded bg-[#D71920]/8 flex items-center justify-center">
            <Building2 size={20} className="text-[#D71920]" />
          </div>
          <div>
            <div className="text-xs font-bold text-[#D4AF37] uppercase tracking-widest mb-0.5">Portal Perguruan</div>
            <h1 className="text-2xl font-extrabold text-[#0B0B0B]">Profil Perguruan</h1>
            <p className="text-sm text-[#777] mt-0.5">{school?.name}</p>
          </div>
        </div>

        <div className="h-px bg-[#E5E5E5]" />

        <form onSubmit={handleSubmit} className="bg-white rounded border border-[#E5E5E5] p-5 sm:p-6 space-y-5" data-testid="profile-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">{field('name', 'Nama Perguruan')}</div>
            {field('head_coach_name', 'Ketua Jurulatih')}
            {field('phone', 'No. Telefon', 'tel')}
            <div className="sm:col-span-2">{field('email', 'Emel', 'email')}</div>
            {field('division', 'Bahagian')}
            {field('district', 'Daerah')}
            <div className="sm:col-span-2">{field('training_address', 'Alamat Latihan')}</div>
            <div className="sm:col-span-2">
              <Label htmlFor="gelanggang" className="text-xs font-bold text-[#555] uppercase tracking-wider">
                Nama Gelanggang <span className="text-[#999] text-[10px] font-normal normal-case tracking-normal">(Opsional)</span>
              </Label>
              <Input
                id="gelanggang"
                type="text"
                value={formData.gelanggang}
                onChange={e => setFormData({ ...formData, gelanggang: e.target.value })}
                placeholder="Contoh: Dewan Serbaguna Kampung Buntal"
                className="mt-1.5 border-[#E5E5E5] text-sm"
                data-testid="input-gelanggang"
              />
            </div>
          </div>

          <div className="flex justify-end pt-4 border-t border-[#E5E5E5]">
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 bg-[#D71920] hover:bg-[#b8161c] text-white text-sm font-semibold px-6 py-2.5 rounded transition-colors disabled:opacity-60"
              data-testid="save-button"
            >
              <Save size={14} />
              {saving ? 'Menyimpan...' : 'Simpan Perubahan'}
            </button>
          </div>
        </form>
      </div>
    </SchoolLayout>
  );
};

export default Profile;
